import { useMemo, useState } from 'react';

import { Close } from '@mui/icons-material';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Column } from 'react-table';

import { fetchTranches, sendForSignatureTranche } from 'api';
import { LinkButton } from 'components';
import { Table } from 'components/Table';
import {
  convertToDateShort,
  getErrorDetail,
  getLabel,
  getPageCount,
  trancheStatusObject,
} from 'helpers';
import { useUrlPagination, useControlledPagination } from 'hooks';
import { useSnackContext } from 'hooks/useSnackContext';
import { DerogCell } from 'pages/tranche/DerogCell';
import { TrancheProps } from 'types';

import {
  DashboardWrapper,
  CellWrapper,
  NullValueMarker,
} from '../styledComponents';

export const AdminTrancheTable = () => {
  const location = useLocation();
  const urlFrom = useMemo(
    () => location.pathname + location.search,
    [location.pathname, location.search],
  );
  const { setSnack } = useSnackContext();

  const { controlledRows, controlledPageIndex } = useUrlPagination();
  const config = useMemo(
    () => ({
      params: {
        page_number: controlledPageIndex,
        limit: controlledRows,
      },
    }),
    [controlledPageIndex, controlledRows],
  );
  const { dataArray, dataLoaded, count, fetchData, updateData } =
    useControlledPagination(fetchTranches, config);

  const [sendToSignerAlert, setSendToSignerAlert] =
    useState<TrancheProps | null>(null);

  const columns = useMemo<Array<Column<TrancheProps>>>(
    () => [
      {
        id: 'add',
        Header: () => {
          return (
            <CellWrapper aria-hidden>
              <p aria-label='Inspect tranche' />
            </CellWrapper>
          );
        },
        Cell: (row: any) => {
          const tranche = row.row.original as TrancheProps;
          return (
            <CellWrapper>
              <LinkButton
                variant='text'
                aria-label={'Inspect Tranche number ' + row.row.original.id}
                to={{
                  pathname: 'reviewed_candidates',
                  search: `tranche_id=${tranche.id}&include_siv_ineligible=1`,
                  state: {
                    from: urlFrom,
                  },
                }}
              >
                INSPECT
              </LinkButton>
            </CellWrapper>
          );
        },
      },
      // {
      //   id: 'tranche_number',
      //   Header: 'Old Tranche #',
      //   accessor: 'id',
      //   disableFilters: true,
      //   disableSortBy: false,
      // },
      {
        id: 'tranche_sequence',
        Header: 'Tranche #',
        accessor: 'sequence_no',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'sent',
        Header: 'Sent To State',
        Cell: (row: any) => {
          const { signature_time } = row.row.original as TrancheProps;

          return signature_time ? (
            <Typography variant='body1'>
              {convertToDateShort(signature_time)}
            </Typography>
          ) : (
            <NullValueMarker />
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'candidate_count',
        Header: '# of Entries',
        accessor: 'candidate_count',
        width: 100,
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'clear',
        Header: 'Clear | Derog',
        Cell: (row: any) => {
          const tranche = row.row.original as TrancheProps;

          return <DerogCell {...tranche} />;
        },
        width: 100,
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'status',
        Header: 'Status',
        accessor: 'status',

        Cell: (row: any) => {
          const { status } = row.row.original;

          return status ? (
            <Typography variant='body1'>
              {getLabel({ value: status, options: trancheStatusObject })}
            </Typography>
          ) : (
            <NullValueMarker />
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'action',
        Header: '',
        accessor: 'status',
        Cell: (row: any) => {
          const { status, candidate_count } = row.row.original as TrancheProps;

          switch (status) {
            case 'active':
              return (
                <Button
                  variant='outlined'
                  disabled={candidate_count === 0}
                  onClick={() => setSendToSignerAlert(row.row.original)}
                >
                  SEND FOR SIGNATURE
                </Button>
              );
            default:
              return null;
          }
        },
        disableFilters: true,
        disableSortBy: false,
      },
    ],
    [urlFrom],
  );
  return (
    <>
      <DashboardWrapper>
        <Table<TrancheProps>
          count={count}
          loading={!dataLoaded}
          useTableOptions={{
            data: dataArray,
            columns: columns,
            manualPagination: true,
            pageCount: getPageCount(count, controlledRows),
            autoResetPage: false,
            disableSort: true,
          }}
        />
      </DashboardWrapper>
      <Dialog open={!!sendToSignerAlert}>
        <IconButton
          aria-label='cancel'
          sx={{ marginLeft: 'auto' }}
          onClick={() => setSendToSignerAlert(null)}
        >
          <Close fontSize='small' />
        </IconButton>
        <Box sx={{ padding: 3 }}>
          <Typography
            sx={{ mb: 1 }}
            variant='h5'
          >{`You are sending Tranche ${sendToSignerAlert?.sequence_no} to the Signer`}</Typography>
          <Typography sx={{ mb: 2 }} variant='body1'>
            Clicking 'Send' below will make this Tranche available to the
            Signer.
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant='contained'
              sx={{ mr: 1 }}
              disabled={!sendToSignerAlert}
              onClick={async () => {
                if (sendToSignerAlert) {
                  try {
                    const { data } = await sendForSignatureTranche(
                      sendToSignerAlert.id,
                    );
                    updateData(data);
                    fetchData();
                  } catch (e) {
                    console.log(e);
                    setSnack((props) => ({
                      ...props,
                      severity: 'error',
                      open: true,
                      message: getErrorDetail({
                        error: e,
                        fallback: 'Failed To Send For Signature',
                      }),
                      error: e as Error,
                    }));
                  }
                  setSendToSignerAlert(null);
                }
              }}
            >
              Send To Signer
            </Button>
            <Button
              sx={{ ml: 1 }}
              variant='outlined'
              onClick={() => setSendToSignerAlert(null)}
            >
              CANCEL
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};
